import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { adminCompanyAPI } from '../../api/axios';
import { toast } from 'react-toastify';
import AdminNav from '../../components/AdminNav';
import {
  Building2, MapPin, FileText, CheckCircle, XCircle,
  Loader2, AlertCircle, Phone, Mail, Hash
} from 'lucide-react';

function InfoRow({ label, value }) {
  return (
    <div>
      <p className="text-xs text-gray-400 uppercase tracking-wide font-medium mb-0.5">{label}</p>
      <p className="text-sm font-semibold text-slate-800">{value || '—'}</p>
    </div>
  );
}

export default function AdminCompanyReview() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [rejectReason, setRejectReason] = useState('');

  useEffect(() => { load(); }, [id]);

  const load = async () => {
    try {
      const res = await adminCompanyAPI.getCompany(id);
      setCompany(res.data.data);
    } catch {
      toast.error('Failed to load company details');
      navigate('/admin/pending-companies');
    } finally { setLoading(false); }
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      await adminCompanyAPI.approveCompany(id);
      toast.success('Company approved!');
      navigate('/admin/pending-companies');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to approve'); }
    finally { setActionLoading(false); }
  };

  const handleReject = async () => {
    if (!rejectReason.trim()) { toast.error('Please provide a rejection reason'); return; }
    setActionLoading(true);
    try {
      await adminCompanyAPI.rejectCompany(id, rejectReason);
      toast.success('Company rejected');
      navigate('/admin/pending-companies');
    } catch (e) { toast.error(e.response?.data?.message || 'Failed to reject'); }
    finally { setActionLoading(false); setShowRejectModal(false); }
  };

  if (loading) return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <Loader2 className="h-10 w-10 animate-spin text-teal-600" />
    </div>
  );

  if (!company) return null;

  const isPending = company.status === 'PENDING';
  const orgLabel = company.organizationType === 'HOSPITAL' ? 'Hospital' : 'Company';

  const statusColors = {
    PENDING:  'bg-yellow-50 border-yellow-200 text-yellow-800',
    APPROVED: 'bg-green-50  border-green-200  text-green-800',
    REJECTED: 'bg-red-50    border-red-200    text-red-800',
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <AdminNav
        title={company.companyName}
        subtitle={`${orgLabel} · ${company.status}`}
        backPath="/admin/pending-companies"
      />

      {/* Sticky action bar */}
      {isPending && (
        <div className="sticky top-16 z-10 bg-white border-b border-gray-100 shadow-sm">
          <div className="max-w-4xl mx-auto px-6 py-3 flex items-center justify-between">
            <p className="text-sm text-gray-500">Review the company profile before taking action.</p>
            <div className="flex gap-3">
              <button onClick={() => setShowRejectModal(true)} disabled={actionLoading}
                className="flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200
                  px-5 py-2 rounded-xl font-bold text-sm hover:bg-red-100 transition disabled:opacity-50">
                <XCircle className="h-4 w-4" /> Reject
              </button>
              <button onClick={handleApprove} disabled={actionLoading}
                className="flex items-center gap-1.5 bg-green-600 text-white px-5 py-2 rounded-xl
                  font-bold text-sm hover:bg-green-700 transition disabled:opacity-50
                  shadow-lg shadow-green-500/20">
                {actionLoading
                  ? <Loader2 className="h-4 w-4 animate-spin" />
                  : <CheckCircle className="h-4 w-4" />}
                Approve
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto px-6 py-8 space-y-5">

        {/* Status banner */}
        <div className={`rounded-xl p-4 flex items-center gap-3 border ${statusColors[company.status] || statusColors.PENDING}`}>
          <AlertCircle className="h-5 w-5 shrink-0" />
          <div>
            <p className="font-bold text-sm">Status: {company.status}</p>
            <p className="text-xs opacity-75 mt-0.5">
              Registered {new Date(company.createdAt).toLocaleDateString('en-IN', {
                day: 'numeric', month: 'long', year: 'numeric'
              })}
            </p>
          </div>
        </div>

        {/* Company identity */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center">
              <Building2 className="h-4 w-4 text-blue-600" />
            </div>
            <h2 className="font-bold text-slate-800">Company Details</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InfoRow label="Company Name"      value={company.companyName} />
            <InfoRow label="Organisation Type" value={orgLabel} />
            <InfoRow label="Contact Person"    value={company.contactPersonName} />
            <InfoRow label="GST Number"        value={company.gstNumber} />
            <InfoRow label="PAN Number"        value={company.panNumber} />
          </div>
        </div>

        {/* Contact */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-teal-50 rounded-lg flex items-center justify-center">
              <Phone className="h-4 w-4 text-teal-600" />
            </div>
            <h2 className="font-bold text-slate-800">Contact Information</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <InfoRow label="Phone"  value={company.contactPhone} />
            <InfoRow label="Email"  value={company.contactEmail} />
          </div>
        </div>

        {/* Address */}
        <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
          <div className="flex items-center gap-2 mb-5">
            <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center">
              <MapPin className="h-4 w-4 text-red-500" />
            </div>
            <h2 className="font-bold text-slate-800">Address</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="md:col-span-2">
              <InfoRow label="Address" value={company.address} />
            </div>
            <InfoRow label="City"    value={company.city} />
            <InfoRow label="State"   value={company.state} />
            <InfoRow label="Pincode" value={company.pincode} />
          </div>
        </div>

        {/* Rejection reason if already rejected */}
        {company.status === 'REJECTED' && company.rejectionReason && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-5">
            <div className="flex items-center gap-2 mb-2">
              <XCircle className="h-5 w-5 text-red-500" />
              <h2 className="font-bold text-red-800">Rejection Reason</h2>
            </div>
            <p className="text-sm text-red-700">{company.rejectionReason}</p>
          </div>
        )}

        {/* Bottom actions */}
        {isPending && (
          <div className="flex justify-end gap-3 pb-4">
            <button onClick={() => setShowRejectModal(true)} disabled={actionLoading}
              className="flex items-center gap-1.5 bg-red-50 text-red-600 border border-red-200
                px-6 py-3 rounded-xl font-bold text-sm hover:bg-red-100 transition disabled:opacity-50">
              <XCircle className="h-5 w-5" /> Reject
            </button>
            <button onClick={handleApprove} disabled={actionLoading}
              className="flex items-center gap-1.5 bg-green-600 text-white px-6 py-3 rounded-xl
                font-bold text-sm hover:bg-green-700 transition disabled:opacity-50
                shadow-lg shadow-green-500/20">
              {actionLoading
                ? <Loader2 className="h-5 w-5 animate-spin" />
                : <CheckCircle className="h-5 w-5" />}
              Approve Company
            </button>
          </div>
        )}
      </div>

      {/* Reject Modal */}
      {showRejectModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 px-4">
          <div className="bg-white rounded-2xl shadow-2xl p-6 max-w-md w-full">
            <h3 className="font-bold text-slate-800 mb-1">Reject Company</h3>
            <p className="text-sm text-gray-400 mb-4">
              This will reject <span className="font-semibold text-slate-700">{company.companyName}</span>'s registration.
            </p>
            <textarea value={rejectReason} onChange={(e) => setRejectReason(e.target.value)}
              placeholder="Enter reason for rejection..."
              rows={3}
              className="w-full border border-gray-200 rounded-xl px-4 py-3 text-sm bg-gray-50
                focus:outline-none focus:ring-2 focus:ring-red-500 resize-none" />
            <div className="flex gap-3 mt-4">
              <button onClick={() => { setShowRejectModal(false); setRejectReason(''); }}
                className="flex-1 py-2.5 border border-gray-200 rounded-xl font-semibold text-sm
                  text-gray-600 hover:bg-gray-50">
                Cancel
              </button>
              <button onClick={handleReject} disabled={actionLoading}
                className="flex-1 py-2.5 bg-red-600 text-white rounded-xl font-bold text-sm
                  hover:bg-red-700 disabled:opacity-50">
                {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
